import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { scopeFor } from '@/modules/backups/scope';
import type { BackupScope, BackupSource } from '@/types/api';

/**
 * What a backup covers.
 *
 * Worth its own control rather than a checkbox, because the two are not "with or without files" to
 * a customer — they are two different jobs. A full copy of a large site is slow and expensive, so it
 * runs nightly; the database is small and changes constantly, so it can run far more often. The
 * description under each option is the part that makes the choice obvious.
 */
export function ScopePicker({
  id,
  value,
  onChange,
  source,
  disabled,
  note,
}: {
  id: string;
  value: BackupScope;
  onChange: (scope: BackupScope) => void;
  /** The chosen source, when there is one: it can leave only one scope that makes sense. */
  source?: BackupSource | undefined;
  disabled?: boolean | undefined;
  /** Replaces the description, for when the choice cannot be made here. */
  note?: string | undefined;
}) {
  const effective = scopeFor(source, value);
  const onlyOne = source !== undefined && (!source.has_directory || !source.has_database);

  const sourceNote =
    source === undefined || !onlyOne
      ? undefined
      : !source.has_directory
        ? 'This source is a database only, so that is what is backed up.'
        : 'This source has no database, so its directory is what is backed up.';

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>What to back up</Label>
      <Select
        id={id}
        value={effective}
        onChange={(event) => onChange(event.target.value as BackupScope)}
        disabled={disabled === true || onlyOne}
      >
        <option value="full">{source !== undefined && !source.has_database ? 'Directory' : 'Directory and database'}</option>
        <option value="database">Database only</option>
      </Select>
      <p className="text-xs text-muted-foreground">
        {note ??
          sourceNote ??
          (effective === 'database'
            ? 'Just the database. Small and quick, so it can run often. Restoring it never touches the files.'
            : 'The directory and its database. Restores both.')}
      </p>
    </div>
  );
}
